'use client'

/**
 * ResultSuccessCard - bounded "peak moment" frame for calculator results.
 *
 * Growth-design rule 1 (peak-end): the screen right after a user completes a
 * calculation is the most leverage-able pixel in the tool. This card pairs the
 * three things that make that moment land, in one frame:
 *   1. a hero metric (the key number + unit, large),
 *   2. a one-sentence plain-English interpretation that names the specifics
 *      (grass / ZIP / size / season where the tool has them),
 *   3. a single primary next-action CTA (don't stack competing CTAs).
 *
 * It is intentionally generic so every live-update tool can drop it in as the
 * FIRST post-result element, above the data chart. Tools own the copy and the
 * CTA target; the card owns the layout and visual treatment.
 *
 * Usage:
 *   <ResultSuccessCard
 *     heroValue="4.3"
 *     heroUnit="lbs"
 *     heroLabel="of fertilizer to spread"
 *     interpretation="Apply 4.3 lbs across your 5,000 sq ft Bermuda lawn this spring."
 *     cta={{ href: '/tools/watering-schedule?...', label: 'Plan your watering schedule' }}
 *   />
 */

import * as React from 'react'
import Link from 'next/link'
import { Card } from '@/components/ui/card'
import { CheckCircle2, ArrowRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { trackEvent } from '@/lib/analytics'
import { useAnimatedNumber } from '@/hooks/use-animated-number'
import { UnifiedShareButton } from './unified-share-button'

/** One reframed view of the hero metric (e.g. inches/week vs gallons/week). */
export interface ResultSuccessCardReframeView {
  /** The headline number, pre-formatted. Replaces heroValue when this view is active. */
  value: string
  /** Unit shown beside the value. Replaces heroUnit. */
  unit?: string
  /** Short label under the hero metric. Replaces heroLabel. */
  label?: string
}

/**
 * Optional in-result reframe toggle. When present, the card renders a small
 * segmented pill at the top of the hero that swaps heroValue/heroUnit/heroLabel
 * live between named views — without sending the user back to the form. Matches
 * the cost-estimator "Per year / Per month" idiom (growth-design rule 1: keep
 * the user on the peak-moment screen).
 */
export interface ResultSuccessCardReframe {
  /** Toggle options in display order. `key` must be a key of `values`. */
  options: { key: string; label: string }[]
  /** Hero content per option key. */
  values: Record<string, ResultSuccessCardReframeView>
  /** Which option is active on first render. */
  defaultKey: string
  /** Accessible label for the toggle group. */
  ariaLabel?: string
  /** Analytics event fired on toggle. Receives `{ view: <key> }` plus `analyticsProps`. */
  analyticsEvent?: string
  /** Extra props merged into the analytics payload (e.g. grass_type). */
  analyticsProps?: Record<string, unknown>
}

/**
 * One glanceable secondary metric, rendered as a small chip-tile in a row
 * between the interpretation and the CTA. Turns supporting numbers the tool
 * already computed into recognition-first "trophies" instead of prose
 * (growth-design rule 3: on dense surfaces, recognition beats recall).
 */
export interface ResultSuccessCardStat {
  /** Optional lucide icon for the tile (subtle, muted green). */
  icon?: LucideIcon
  /** Tiny uppercase caption naming what the number is (e.g. "Per month"). */
  label: string
  /** The metric itself, pre-formatted and prominent (e.g. "$1,240"). */
  value: string
}

/**
 * Optional share affordance for the success card. When present, a share button
 * renders top-right of the card (aligned with the eyebrow) so the peak-moment
 * frame doubles as a viral loop — recipients land on the prefilled tool URL.
 * Absent → the card renders exactly as before.
 */
export interface ResultSuccessCardShare {
  /** Prefilled tool URL (build with buildToolUrl so the recipient sees the same result). */
  url: string
  /** Share-sheet title. */
  title: string
  /** Share-sheet body text naming the specifics. */
  text: string
  /** Tool identifier for `calculator_shared` analytics, e.g. `'seeding-calculator'`. */
  trackingId?: string
}

export interface ResultSuccessCardCta {
  /** Destination URL (prefer a prefilled tool URL from buildToolUrl/buildRelatedToolUrl). */
  href: string
  /** Specific, benefit-named action. "Plan your watering schedule" beats "Next". */
  label: string
  /** Optional click handler for analytics. */
  onClick?: () => void
}

interface ResultSuccessCardProps {
  /**
   * The key number, pre-formatted (e.g. "4.3", "1.2", "Sep 15"). Optional:
   * omit it for results that have no single headline number (a deliberate
   * skip, a date window, a text recommendation). When absent, the card shows
   * just the eyebrow + interpretation + CTA so a "0" never reads as a result.
   */
  heroValue?: string
  /**
   * Optional numeric value to COUNT UP to (0 → heroCountTo) when the result
   * first appears — a small peak-moment delight. Opt-in: when omitted, the
   * hero renders the static `heroValue` string exactly as before. When set, it
   * animates and replaces `heroValue` for display (pass `heroValue` too as the
   * reduced-motion / no-format fallback). Respects prefers-reduced-motion (the
   * hook snaps to the target). Ignored while a `reframe` view is active (the
   * reframe owns the hero with pre-formatted strings).
   */
  heroCountTo?: number
  /** Decimal places for the counted value (default 0). */
  heroCountDecimals?: number
  /** Formats the animated number for display (e.g. n => `$${Math.round(n).toLocaleString()}`). Defaults to the raw number. */
  heroCountFormat?: (n: number) => string
  /** Unit shown beside the value (e.g. "lbs", "in/wk"). Ignored when heroValue is omitted. */
  heroUnit?: string
  /** Short label under the hero metric describing what the number is. Ignored when heroValue is omitted. */
  heroLabel?: string
  /** One-sentence plain-English interpretation naming the user's specifics. */
  interpretation: React.ReactNode
  /**
   * Optional in-result reframe toggle. When present, it owns the hero number/
   * unit/label (overriding heroValue/heroUnit/heroLabel) and renders a segmented
   * pill above the hero. When absent, the card renders exactly as before.
   */
  reframe?: ResultSuccessCardReframe
  /**
   * Optional row of glanceable secondary metrics, rendered as small chip-tiles
   * between the interpretation and the CTA. When absent or empty, the card
   * renders exactly as before. Keep to 2-3 decision-useful numbers the tool
   * already computed; this is recognition-first reinforcement, not a data dump.
   */
  stats?: ResultSuccessCardStat[]
  /** Single primary next-action. Omit only when no sensible next step exists. */
  cta?: ResultSuccessCardCta
  /** Optional share affordance, rendered top-right of the card. */
  share?: ResultSuccessCardShare
  /** Small eyebrow above the card body. Defaults to "Your result". */
  eyebrow?: string
  /** Icon for the eyebrow. Defaults to a check mark. */
  eyebrowIcon?: LucideIcon
  className?: string
}

/**
 * Bounded success frame: hero metric + named interpretation + one primary CTA.
 */
export function ResultSuccessCard({
  heroValue,
  heroCountTo,
  heroCountDecimals = 0,
  heroCountFormat,
  heroUnit,
  heroLabel,
  reframe,
  interpretation,
  stats,
  cta,
  share,
  eyebrow = 'Your result',
  eyebrowIcon: EyebrowIcon = CheckCircle2,
  className,
}: ResultSuccessCardProps) {
  const [activeKey, setActiveKey] = React.useState(reframe?.defaultKey)

  // When a reframe is supplied, it owns the hero content; otherwise fall back to
  // the static hero props so every existing consumer renders identically.
  const activeView = reframe && activeKey ? reframe.values[activeKey] : undefined

  // Opt-in count-up: when a tool passes heroCountTo and no reframe view is
  // active, animate 0 → target ONCE on appearance, then hand off to the live
  // heroValue string. This matters because some tools (e.g. the cost estimator)
  // recompute the result live as the user edits — animating the live value would
  // re-trigger the count-up on every keystroke. So we count up to the value at
  // MOUNT (captured in a ref, never re-animated) and stop counting shortly after,
  // letting subsequent live updates flow through heroValue. The hook is called
  // unconditionally (React rules) and is a harmless no-op for non-opt-in tools.
  // Reduced motion snaps the hook to the target, so no jarring motion.
  const initialCountTarget = React.useRef(heroCountTo)
  const animatedCount = useAnimatedNumber(initialCountTarget.current ?? 0, 900, heroCountDecimals)
  const [countingActive, setCountingActive] = React.useState(heroCountTo !== undefined)
  React.useEffect(() => {
    if (initialCountTarget.current === undefined) return
    const t = setTimeout(() => setCountingActive(false), 950)
    return () => clearTimeout(t)
  }, [])
  const counting = countingActive && !activeView
  const countedValue = heroCountFormat ? heroCountFormat(animatedCount) : String(animatedCount)

  const shownValue = activeView
    ? activeView.value
    : counting
      ? countedValue
      : heroValue
  const shownUnit = activeView ? activeView.unit : heroUnit
  const shownLabel = activeView ? activeView.label : heroLabel

  return (
    <Card
      // Marks the result region. Added so the mobile app sticky bar could stand
      // down while a result was on screen; that bar is gone (tool pages no
      // longer take a fixed bottom overlay at all, 2026-07-25). Kept as the
      // stable "this is the answer" hook for any future overlay guard or e2e
      // selector — cheaper to keep than to re-derive.
      data-tool-result=""
      className={cn(
        'overflow-hidden border border-green-200 bg-linear-to-br from-green-50 to-white shadow-sm',
        // Peak-moment reveal: the result card fades + slides up when it mounts
        // (submit-gated tools = a real reveal; live tools = a gentle settle on
        // load). CSS animation runs once on mount, so live recalcs don't replay
        // it. Disabled under prefers-reduced-motion.
        'animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none',
        className,
      )}
    >
      <div className="p-5 sm:p-6">
        {/* Eyebrow + optional share. justify-between keeps the share top-right
            of the peak frame; absent share leaves the eyebrow exactly as before. */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-semibold text-green-700">
            <EyebrowIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {eyebrow}
          </div>
          {share && (
            <UnifiedShareButton
              url={share.url}
              title={share.title}
              text={share.text}
              trackingId={share.trackingId}
              size="sm"
            />
          )}
        </div>

        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-8">
          {/* Hero metric — only when a single headline number exists */}
          {shownValue !== undefined && (
            <div className="shrink-0">
              {/* In-result reframe toggle: swap the hero between named views
                  (e.g. inches/week vs gallons/week) without re-running the form. */}
              {reframe && (
                <div
                  role="group"
                  aria-label={reframe.ariaLabel ?? 'Reframe the result'}
                  className="mb-3 inline-flex rounded-full border border-green-200 bg-white p-0.5"
                >
                  {reframe.options.map((opt) => {
                    const active = opt.key === activeKey
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          setActiveKey(opt.key)
                          if (reframe.analyticsEvent) {
                            trackEvent(reframe.analyticsEvent, {
                              view: opt.key,
                              ...reframe.analyticsProps,
                            })
                          }
                        }}
                        aria-pressed={active}
                        className={cn(
                          'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                          active
                            ? 'bg-green-600 text-white shadow-sm'
                            : 'text-green-700 hover:text-green-800',
                        )}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              )}
              <div className="flex items-baseline gap-1.5">
                <span
                  className={cn(
                    'text-5xl font-extrabold leading-none tracking-tight text-green-700 sm:text-6xl',
                    counting && 'tabular-nums',
                  )}
                >
                  {shownValue}
                </span>
                {shownUnit && (
                  <span className="text-xl font-bold text-green-700/80">{shownUnit}</span>
                )}
              </div>
              {shownLabel && (
                <p className="mt-1.5 text-sm font-medium text-gray-500">{shownLabel}</p>
              )}
            </div>
          )}

          {/* Interpretation — drops the divider when there is no hero beside it */}
          <p
            className={cn(
              'text-pretty text-base leading-relaxed text-gray-700',
              shownValue !== undefined && 'sm:border-l sm:border-green-200/70 sm:pl-8',
            )}
          >
            {interpretation}
          </p>
        </div>

        {/* Secondary stat tiles — glanceable trophies for numbers the tool
            already computed (recognition over recall). Renders only when the
            consumer opts in; absent/empty leaves the card byte-identical. */}
        {stats && stats.length > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {stats.map((stat, i) => {
              const StatIcon = stat.icon
              return (
                <div
                  key={stat.label}
                  className="rounded-xl border border-green-200/70 bg-white/70 px-3 py-2.5 animate-in fade-in duration-500 motion-reduce:animate-none"
                  style={{ animationDelay: `${150 + i * 80}ms`, animationFillMode: 'both' }}
                >
                  <div className="flex items-center gap-1.5 text-gray-500">
                    {StatIcon && (
                      <StatIcon className="h-3.5 w-3.5 text-green-600/80" aria-hidden="true" />
                    )}
                    <span className="text-[0.65rem] font-semibold uppercase tracking-wide">
                      {stat.label}
                    </span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-green-700">{stat.value}</p>
                </div>
              )
            })}
          </div>
        )}

        {/* Single primary next-action */}
        {cta && (
          <Link
            href={cta.href}
            onClick={cta.onClick}
            className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 sm:w-auto"
          >
            {cta.label}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        )}
      </div>
    </Card>
  )
}

export default ResultSuccessCard
